import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { SiteLogo } from '../ui/SiteLogo';
import { PublicFooter } from './PublicFooter';
import { HeaderMobileMenu, mobileMenuItemClass } from './HeaderMobileMenu';
import { useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { buildLocalizedPath } from '../../seo/urls';

export const PublicPageShell = ({ children, activePath = '/' }) => {
  const { t, language } = useI18n();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);

  const navItems = [
    { label: t('navHome'), to: buildLocalizedPath('/', language), match: '/' },
    { label: t('navPricing'), to: buildLocalizedPath('/pricing', language), match: '/pricing' },
    { label: t('navAbout'), to: buildLocalizedPath('/about', language), match: '/about' }
  ];

  const isActive = (item) => {
    if (item.match === '/') {
      return activePath === '/';
    }

    return activePath.startsWith(item.match);
  };

  return (
    <div className="relative isolate min-h-screen overflow-hidden bg-app px-4 text-text sm:px-6 lg:px-8">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute left-1/2 top-[-18rem] h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-accent-soft blur-3xl" />
        <div className="absolute bottom-[-20rem] right-[-8rem] h-[34rem] w-[34rem] rounded-full bg-teal-400/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(15,61,145,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(15,61,145,0.045)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:linear-gradient(to_bottom,black,transparent_80%)] dark:bg-[linear-gradient(rgba(104,213,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(104,213,255,0.04)_1px,transparent_1px)]" />
      </div>

      <div className="relative mx-auto flex min-h-full max-w-6xl flex-col">
        <header className="flex items-center justify-between gap-3 py-4 sm:py-5">
          <Link to={buildLocalizedPath('/', language)} className="flex min-w-0 items-center gap-3">
            <SiteLogo />
            <div className="text-start">
              <p className="text-sm font-bold tracking-tight">{t('landingBrand')}</p>
              <p className="text-xs text-muted">{t('landingTopTag')}</p>
            </div>
          </Link>

          <nav
            className="hidden items-center justify-center rounded-full border border-border bg-panel/80 p-1 shadow-sm backdrop-blur-xl lg:flex"
            aria-label={t('navigation')}
          >
            {navItems.map((item) => (
              <Link
                key={item.match}
                to={item.to}
                aria-current={isActive(item) ? 'page' : undefined}
                className={
                  isActive(item)
                    ? 'rounded-full bg-accent px-4 py-2 text-sm font-semibold text-white dark:text-slate-950'
                    : 'rounded-full px-4 py-2 text-sm font-semibold text-muted transition hover:bg-accent-soft hover:text-text'
                }
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex shrink-0 items-center justify-end gap-2">
            <HeaderMobileMenu
              open={mobileNavOpen}
              onOpen={openMobileNav}
              onClose={closeMobileNav}
              brandTitle={t('landingBrand')}
              brandSubtitle={t('landingTopTag')}
            >
              {navItems.map((item) => (
                <Link
                  key={item.match}
                  to={item.to}
                  aria-current={isActive(item) ? 'page' : undefined}
                  className={mobileMenuItemClass(isActive(item))}
                >
                  {item.label}
                </Link>
              ))}
            </HeaderMobileMenu>

            <Link
              to={isAuthenticated ? '/home' : '/login'}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-accent px-3 text-sm font-bold text-white shadow-sm transition hover:opacity-95 dark:text-slate-950 sm:px-4"
              aria-label={t('account')}
            >
              <UserRound size={16} />
              <span className="hidden sm:inline">{t('account')}</span>
            </Link>
          </div>
        </header>

        <main className="flex-1">{children}</main>

        <PublicFooter />
      </div>
    </div>
  );
};
